// components/ErrorBoundary.tsx
import React, { Component, ErrorInfo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw, Home, HelpCircle } from "lucide-react";
import { GlassCard, GlassButton, GlassAlert } from "./ui/GlassComponents";

// Glassmorphic fallback UI with French content and football context
const FallbackUI = ({ onReset }: { onReset: () => void }) => {
  const { t } = useTranslation("common");
  return (
  <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
    {/* Background pattern overlay */}
    <div className="fixed inset-0 opacity-10">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 35px,
            rgba(16, 185, 129, 0.05) 35px,
            rgba(16, 185, 129, 0.05) 70px
          )`
        }}
      />
    </div>

    <div className="relative z-10 max-w-2xl w-full space-y-6">
      {/* Main error card */}
      <GlassCard className="backdrop-blur-xl">
        <div className="text-center space-y-6 p-8">
          {/* Icon with pulsing animation */}
          <div className="relative mx-auto w-24 h-24">
            <div className="absolute inset-0 bg-red-500/20 rounded-full animate-ping" />
            <div className="relative bg-red-500/10 backdrop-blur-sm border border-red-500/30 rounded-full p-6 flex items-center justify-center">
              <AlertTriangle className="h-12 w-12 text-red-400" />
            </div>
          </div>

          {/* Error title */}
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {t("error_boundary.title")}
            </h1>
            <p className="text-lg text-white/60">
              {t("error_boundary.subtitle")}
            </p>
          </div>

          {/* Error message */}
          <GlassAlert variant="danger" className="text-start">
            <div className="space-y-2">
              <p className="text-white/90 font-medium">
                {t("error_boundary.description")}
              </p>
              <p className="text-white/70 text-sm">
                {t("error_boundary.detail")}
              </p>
            </div>
          </GlassAlert>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <GlassButton
              variant="primary"
              size="lg"
              onClick={onReset}
              className="group"
            >
              <RefreshCw className="h-5 w-5 me-2 group-hover:rotate-180 transition-transform duration-500" />
              {t("error_boundary.refresh_btn")}
            </GlassButton>

            <GlassButton
              variant="secondary"
              size="lg"
              onClick={() => window.location.href = '/'}
              className="group"
            >
              <Home className="h-5 w-5 me-2 group-hover:scale-110 transition-transform" />
              {t("error_boundary.home_btn")}
            </GlassButton>
          </div>

          {/* Help section */}
          <div className="pt-4 border-t border-white/10">
            <div className="flex items-center justify-center text-white/50 text-sm space-x-2 rtl:space-x-reverse">
              <HelpCircle className="h-4 w-4" />
              <span>{t("error_boundary.help_text")}</span>
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Additional info card */}
      <GlassCard className="backdrop-blur-md">
        <div className="p-6 space-y-4">
          <h3 className="text-white/90 font-semibold flex items-center gap-2">
            <span className="text-emerald-400">●</span>
            {t("error_boundary.tips_title")}
          </h3>
          <ul className="space-y-2 text-white/60 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-emerald-400/60 mt-0.5">▸</span>
              <span>{t("error_boundary.tip_internet")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400/60 mt-0.5">▸</span>
              <span>{t("error_boundary.tip_cache")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400/60 mt-0.5">▸</span>
              <span>{t("error_boundary.tip_browser")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400/60 mt-0.5">▸</span>
              <span>{t("error_boundary.tip_extensions")}</span>
            </li>
          </ul>
        </div>
      </GlassCard>
    </div>
  </div>
  );
};

// Define the types for the component's props and state
interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  // This lifecycle method updates the state so the next render will show the fallback UI.
  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  // This lifecycle method is for side effects like logging the error.
  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);

    // You could send error to logging service here
    // Example: logErrorToService(error, errorInfo);
  }

  // Reset the error boundary
  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public render() {
    // If an error was caught, render the fallback UI.
    if (this.state.hasError) {
      return <FallbackUI onReset={this.handleReset} />;
    }

    // Otherwise, render the children as normal.
    return this.props.children;
  }
}

export default ErrorBoundary;
